import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '../../../../Input/Button';
import { ArrowLeft, ArrowRight } from '../../../../Icons';
import { type WizardStepType } from '../..';
import { WizardButtonsContainer } from './styles';

export const WizardButtons = ({
  currentStep,
  submitButtonText,
  onChangeStep,
  amountOfSteps,
  onSubmit,
  nextStepDisabled,
  onBackOut,
  onBackOutText,

  steps,
}: {
  nextStepDisabled?: boolean;
  submitButtonText?: string;
  amountOfSteps: number;
  currentStep: number;
  onBackOutText?: string;
  onSubmit: () => Promise<void>;
  onBackOut?: () => void;
  onChangeStep?: (step: number, previousStep: number) => void;

  steps: WizardStepType[];
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === amountOfSteps - 1;
  const nextStep = currentStep + 1;
  const previousStep = currentStep - 1;
  const nextIsSkippable = steps[nextStep]?.skipCondition?.();

  const defaultNextText = t('components.wizard.buttons.next');
  const skipText = t('components.wizard.buttons.skip');
  const stepNextText = steps[currentStep]?.nextButtonText ?? defaultNextText;
  const nextText = isLastStep && submitButtonText ? submitButtonText : stepNextText;
  const prevText = t('components.wizard.buttons.back');

  const changeStep = async (newStep: number) => {
    if (isLastStep && newStep > currentStep) {
      setSubmitting(true);
      await onSubmit().finally(() => {
        setSubmitting(false);
      });
    } else {
      const nextIsSkippable = steps[newStep]?.skipCondition?.();

      if (nextIsSkippable) {
        const skipTo = newStep > currentStep ? newStep + 1 : newStep - 1;

        onChangeStep?.(skipTo, currentStep);
      } else {
        onChangeStep?.(newStep, currentStep);
      }

      document.getElementById('wizard-title')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const hideBackButton = isFirstStep && !onBackOut;
  const backOutText = onBackOutText && isFirstStep ? onBackOutText : prevText;

  return (
    <WizardButtonsContainer>
      <div style={{ display: 'flex' }}>
        {!hideBackButton && (
          <Button
            disabled={submitting}
            variant={'tertiary'}
            iconPlacement={'before'}
            icon={<ArrowLeft />}
            testId="wizard-back"
            onClick={() => {
              if (isFirstStep) {
                if (onBackOut) {
                  onBackOut();
                }
              } else {
                void changeStep(previousStep);
              }
            }}
          >
            {backOutText}
          </Button>
        )}
      </div>

      <Button
        icon={isLastStep ? undefined : <ArrowRight />}
        iconPlacement={'after'}
        disabled={nextStepDisabled}
        isLoading={submitting}
        testId={isLastStep ? 'wizard-submit' : 'wizard-next'}
        onClick={() => {
          void changeStep(nextStep);
        }}
      >
        {nextIsSkippable ? skipText : nextText}
      </Button>
    </WizardButtonsContainer>
  );
};
